import { uidGroupRowId } from 'api/canonicalPicker'
import type {
  PickerRow,
  ProductRow,
  StoreUidRow,
  UidGroupRow,
} from 'api/canonicalPicker.types'
import type {
  PriceRange,
  ProductUids,
  SelectionUids,
  UidLine,
  UidRow,
} from 'components/AI/CanonicalPicker/versionTypes'
import type { ProviderVariant } from 'slices/aiConversations/pickerTypes'

const unique = <T>(values: T[]): T[] => Array.from(new Set(values))

const isPresent = <T>(value: T | null): value is T => value !== null

// Option groups can hold hundreds of thousands of variants, too many to spread into Math.min
const priceRangeOf = (variants: ProviderVariant[]): PriceRange | null =>
  variants.reduce<PriceRange | null>((range, { priceCents }) => {
    if (priceCents === null) return range
    if (!range) return { minCents: priceCents, maxCents: priceCents }
    return {
      minCents: Math.min(range.minCents, priceCents),
      maxCents: Math.max(range.maxCents, priceCents),
    }
  }, null)

const locationIdsOf = (variants: ProviderVariant[]): number[] =>
  unique(variants.map(variant => variant.locationId).filter(isPresent)).sort(
    (a, b) => a - b
  )

/** The UID a variant is grouped under: its group's, or its own when dbt gave it none */
const groupUidOf = (variant: ProviderVariant): string =>
  variant.uidGroup ?? variant.providerUid

/** A variant whose group is keyed by its provider's global UID */
const isGlobalVariant = (variant: ProviderVariant): boolean =>
  variant.uidGroup !== null &&
  (variant.resolvedGlobalUid ?? variant.providerGlobalUid) === variant.uidGroup

/** A Global's store UIDs, one line per store UID and location */
const linesOf = (
  variants: ProviderVariant[],
  fallbackName: string
): UidLine[] =>
  Array.from(
    new Map(
      variants.map(variant => {
        const id = `${variant.provider}:${variant.providerUid}:${variant.locationId}`
        const line: UidLine = {
          id,
          uid: variant.providerUid,
          name: variant.providerName ?? fallbackName,
          priceCents: variant.priceCents,
          locationId: variant.locationId,
        }
        return [id, line]
      })
    ).values()
  )

const rowOfVariants = (
  provider: string,
  uid: string,
  variants: ProviderVariant[],
  fallbackName: string
): UidRow => {
  const isGlobal = variants.some(isGlobalVariant)
  return {
    id: uidGroupRowId(provider, uid),
    provider,
    name:
      variants.find(variant => variant.providerName)?.providerName ??
      fallbackName,
    uid,
    isGlobal,
    priceRange: priceRangeOf(variants),
    locationIds: locationIdsOf(variants),
    lines: isGlobal ? linesOf(variants, fallbackName) : [],
  }
}

// The UIDs most locations carry come first, so a long list starts with what matters
const byReachThenProvider = (a: UidRow, b: UidRow): number =>
  b.locationIds.length - a.locationIds.length ||
  a.provider.localeCompare(b.provider) ||
  a.name.localeCompare(b.name) ||
  a.id.localeCompare(b.id)

/** Every UID group a smart pick's variants fall in */
const productUidsOf = (
  product: ProductRow,
  variants: ProviderVariant[]
): ProductUids => {
  const groups = new Map<string, ProviderVariant[]>()
  variants.forEach(variant => {
    const id = uidGroupRowId(variant.provider, groupUidOf(variant))
    groups.set(id, [...(groups.get(id) ?? []), variant])
  })
  return {
    id: product.id,
    name: product.name,
    rows: Array.from(groups.values())
      .map(group =>
        rowOfVariants(
          group[0].provider,
          groupUidOf(group[0]),
          group,
          product.name
        )
      )
      .sort(byReachThenProvider),
  }
}

/** The products a pick's variants are read under */
export const variantProductIdsOf = (pick: PickerRow): string[] => {
  switch (pick.kind) {
    case 'product':
      return [pick.id]
    case 'uidGroup':
      return pick.productIds ?? [pick.canonicalId]
    case 'storeUid':
      return pick.productIds
    case 'unmatchedUid':
      return []
  }
}

const variantsUnder = (
  pick: PickerRow,
  variantsByProduct: ReadonlyMap<string, ProviderVariant[]>
): ProviderVariant[] =>
  variantProductIdsOf(pick).flatMap(id => variantsByProduct.get(id) ?? [])

/** A picked group is described by its group row; its store UIDs and prices come from its variants */
const rowOfGroup = (
  group: UidGroupRow,
  variants: ProviderVariant[]
): UidRow => {
  const own = variants.filter(
    variant =>
      variant.provider === group.provider && variant.uidGroup === group.uidGroup
  )
  return {
    id: group.id,
    provider: group.provider,
    name: group.name,
    uid: group.uidGroup,
    isGlobal: group.isGlobal,
    priceRange: group.priceRange ?? priceRangeOf(own),
    locationIds: unique(group.locationIds).sort((a, b) => a - b),
    lines: group.isGlobal ? linesOf(own, group.name) : [],
  }
}

/** A store UID picked alone is only itself: its own prices and locations */
const rowOfStoreUid = (
  pick: StoreUidRow,
  variants: ProviderVariant[]
): UidRow => {
  const own = variants.filter(
    variant =>
      variant.provider === pick.provider && variant.providerUid === pick.uid
  )
  return {
    id: pick.id,
    provider: pick.provider,
    name: pick.storeName,
    uid: pick.uid,
    isGlobal: false,
    priceRange: priceRangeOf(own),
    locationIds:
      own.length > 0 ? locationIdsOf(own) : unique(pick.locationIds ?? []),
    lines: [],
  }
}

/**
 * Every UID behind a level's picks. Smart grouping lists each product's UID
 * groups under it; a fixed list lists its picks alone, in the order they
 * were picked, and a uid added though nothing matches it apart.
 */
const selectionVersionsOf = (
  picks: PickerRow[],
  isSpecific: boolean,
  variantsByProduct: ReadonlyMap<string, ProviderVariant[]>,
  groupsById: ReadonlyMap<string, UidGroupRow> = new Map()
): SelectionUids => {
  if (!isSpecific) {
    return {
      mode: 'smart',
      products: picks
        .filter((pick): pick is ProductRow => pick.kind === 'product')
        .map(product =>
          productUidsOf(product, variantsByProduct.get(product.id) ?? [])
        ),
    }
  }

  const rows: UidRow[] = []
  const unmatchedUids: string[] = []
  picks.forEach(pick => {
    const variants = variantsUnder(pick, variantsByProduct)
    if (pick.kind === 'uidGroup') {
      rows.push(rowOfGroup(groupsById.get(pick.id) ?? pick, variants))
    } else if (pick.kind === 'storeUid') {
      rows.push(rowOfStoreUid(pick, variants))
    } else if (pick.kind === 'unmatchedUid') {
      unmatchedUids.push(pick.uid)
    }
  })
  return { mode: 'specific', rows, unmatchedUids }
}

/** The uid groups read by key, by row id */
export const groupsByIdOf = (rows: PickerRow[]): Map<string, UidGroupRow> =>
  new Map(
    rows
      .filter((row): row is UidGroupRow => row.kind === 'uidGroup')
      .map(row => [row.id, row])
  )

/** The variants of each loaded product, by canonical id */
export const variantsByProductOf = (
  rows: PickerRow[]
): Map<string, ProviderVariant[]> =>
  new Map(
    rows
      .filter((row): row is ProductRow => row.kind === 'product')
      .map(row => [row.id, row.variants ?? []])
  )

export default selectionVersionsOf
